import React, { useState, useEffect } from "react";
import { userService } from "../../../_services";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Loader2 } from "lucide-react";

/**
 * Edit Secondary Link Component
 * Modernized with shadcn components - No Dialog wrapper, parent handles Dialog
 */
function EditUserLink({ 
    assigneduser, 
    groupnumber, 
    linktype, 
    linksuffix, 
    objectuid,
    toggle_add_edit_popup,
    updateUserList 
}) {
    const [linkSuffix, setLinkSuffix] = useState(linksuffix || '');
    const [selectedGroupNumber, setSelectedGroupNumber] = useState(groupnumber || '');
    const [initialSuffix] = useState(linksuffix || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        setLinkSuffix(linksuffix || '');
        setSelectedGroupNumber(groupnumber || '');
    }, [linksuffix, groupnumber]);

    const validateLinkSuffix = (value) => {
        if (!value) {
            return 'Link Suffix is required';
        }
        if (value.length < 3 || value.length > 16) {
            return 'Link Suffix must be between 3 and 16 characters';
        }
        const alphanumeric = /^[a-zA-Z0-9]+$/;
        if (!alphanumeric.test(value)) {
            return 'Link Suffix must be alphanumeric only';
        }
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        const validationError = validateLinkSuffix(linkSuffix);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);

        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = Usermeta_Data.split(':');
            
            const apidata = await userService.EditSecondLink(
                userId,
                userName,
                objectuid,
                linkSuffix,
                1, // Flag = 1 for edit
                false // action
            );

            if (apidata && apidata.length > 0) {
                if (apidata[0].statusCode !== -1) {
                    setSuccessMessage(apidata[0].statusMessage || 'Link updated successfully');
                    updateUserList();
                    setTimeout(() => {
                        toggle_add_edit_popup();
                    }, 1000);
                } else {
                    setError(apidata[0].statusMessage || 'Failed to update link');
                }
            } else {
                setError('No response from server. Please try again.');
            }
        } catch (err) {
            console.error('Error updating link:', err);
            setError('Failed to update link. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleLinkSuffixChange = (e) => {
        const value = e.target.value;
        // Only allow alphanumeric characters
        if (value === '' || /^[a-zA-Z0-9]+$/.test(value)) {
            setLinkSuffix(value);
            setError('');
        }
    };

    const isModified = linkSuffix !== initialSuffix;

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
                {/* Assigned User - Read Only */}
                <div className="space-y-2">
                    <Label htmlFor="assignedUser">Assigned User</Label>
                    <Input
                        id="assignedUser"
                        value={assigneduser || ''}
                        readOnly
                        disabled
                        className="bg-gray-50"
                    />
                </div>

                {/* Group Number - Editable Input */}
                <div className="space-y-2">
                    <Label htmlFor="groupNumber">Group Number</Label>
                    <Input
                        id="groupNumber"
                        value={selectedGroupNumber}
                        onChange={(e) => setSelectedGroupNumber(e.target.value)}
                        placeholder="Enter group number"
                    />
                </div>

                {/* Link Type - Read Only */}
                <div className="space-y-2">
                    <Label htmlFor="linkType">Link Type</Label>
                    <Select value={linktype || 'Perpetual'} disabled>
                        <SelectTrigger className="bg-gray-50">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Perpetual">Perpetual</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Link Suffix - Editable */}
                <div className="space-y-2">
                    <Label htmlFor="linkSuffix">
                        Link Suffix <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        id="linkSuffix"
                        value={linkSuffix}
                        onChange={handleLinkSuffixChange}
                        placeholder="Enter 3-16 alphanumeric characters"
                        maxLength={16}
                        className={error ? 'border-red-500' : ''}
                    />
                    <p className="text-xs text-gray-500">
                        {linkSuffix.length}/16 characters (alphanumeric only)
                    </p>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {/* Success Message */}
            {successMessage && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-600">{successMessage}</p>
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button
                    type="button"
                    variant="outline"
                    onClick={toggle_add_edit_popup}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="secondary"
                    disabled={loading || !isModified}
                >
                    {loading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Updating...
                        </>
                    ) : (
                        'Update Link'
                    )}
                </Button>
            </div>
        </form>
    );
}

export default EditUserLink;
